import { useState, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { Plus, Search, Shield, ShieldAlert, ShieldCheck } from "lucide-react"
import { useEquipment } from "@/hooks/use-equipment"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { formatDate } from "@/lib/utils"
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table"
import type { EquipmentItem } from "@/lib/types"

const columnHelper = createColumnHelper<EquipmentItem>()

export function EquipmentListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState("")
  const [categoryFilter, setCategoryFilter] = useState("")

  const { data, isLoading } = useEquipment({
    search: search || undefined,
    category: categoryFilter || undefined,
  })

  const items = data?.data ?? []

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: () => "Urządzenie",
        cell: (info) => (
          <div>
            <span className="font-medium text-foreground">
              {info.getValue()}
            </span>
            {info.row.original.manufacturer && (
              <p className="text-xs text-muted-foreground">
                {info.row.original.manufacturer} {info.row.original.model_name || ""}
              </p>
            )}
          </div>
        ),
      }),
      columnHelper.accessor("serial_number", {
        header: () => "Nr seryjny",
        cell: (info) => (
          <span className="text-sm font-mono text-muted-foreground">
            {info.getValue() || "—"}
          </span>
        ),
      }),
      columnHelper.accessor("category", {
        header: () => "Kategoria",
        cell: (info) => info.getValue() ? (
          <Badge variant="secondary" className="text-xs">{info.getValue()}</Badge>
        ) : <span className="text-muted-foreground">—</span>,
      }),
      columnHelper.accessor("company", {
        header: () => "Kontrahent",
        cell: (info) => {
          const company = info.getValue()
          return (
            <span className="text-sm text-muted-foreground">
              {company ? company.name : "—"}
            </span>
          )
        },
      }),
      columnHelper.accessor("warranty_until", {
        header: () => "Gwarancja",
        cell: (info) => {
          const warrantyDate = info.getValue()
          const isActive = info.row.original.warranty_active

          if (!warrantyDate) return <span className="text-sm text-muted-foreground">—</span>

          return (
            <div className="flex items-center gap-1.5">
              {isActive ? (
                <ShieldCheck size={14} className="text-green-500" />
              ) : (
                <ShieldAlert size={14} className="text-red-500" />
              )}
              <span className={`text-sm ${isActive ? "text-green-500" : "text-red-500"}`}>
                {formatDate(warrantyDate)}
              </span>
            </div>
          )
        },
      }),
      columnHelper.accessor("created_at", {
        header: () => "Dodano",
        cell: (info) => (
          <span className="text-sm text-muted-foreground">
            {formatDate(info.getValue())}
          </span>
        ),
      }),
    ],
    []
  )

  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield size={20} className="text-muted-foreground" />
          <h1 className="page-title">
            Urządzenia
          </h1>
        </div>
        <Button onClick={() => navigate("/equipment/new")}>
          <Plus size={16} /> Dodaj urządzenie
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Szukaj po nazwie, numerze seryjnym..."
            className="h-9 w-full rounded-lg border border-input bg-transparent pl-9 pr-3 text-sm text-foreground outline-none placeholder:text-muted-foreground"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm text-foreground"
        >
          <option value="">Wszystkie kategorie</option>
          <option value="drukarka">Drukarka</option>
          <option value="komputer">Komputer</option>
          <option value="serwer">Serwer</option>
          <option value="switch">Switch</option>
          <option value="ups">UPS</option>
          <option value="telefon">Telefon</option>
        </select>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-input py-12 text-muted-foreground">
          <p className="text-sm">Brak urządzeń</p>
          <Button variant="outline" onClick={() => navigate("/equipment/new")}>
            <Plus size={16} /> Dodaj urządzenie
          </Button>
        </div>
      ) : (
        <div className="surface-card overflow-hidden">
          <table className="w-full">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="bg-muted/30">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground"
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => navigate(`/equipment/${row.original.id}`)}
                  className="cursor-pointer border-t transition-colors hover:bg-muted/30"
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
